import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import {
  HrFlexContainer,
  HrWhite,
  HrFontBold,
  Tablet,
  Separate,
  HrImageContainer,
  HrTitleH5,
  HrIconText,
  HrButtons,
  HrIconButton
} from '@components/styles/hr-styles';
import { useTranslation } from 'react-i18next';
import { BreadCrumb } from './bread-crumb';
import useStore from '../store/useStore';
import { DisplaySettingsContext } from '../store/display-settings';

const MyHeader = styled(HrFlexContainer)`
  z-index: 2;
  height: 56px;
  border-radius: 0;
  justify-content: center;
  padding: 0 24px;

  & .mobile {
    display: none;
  }

  & .mobile .hr-menu-burger,
  & .mobile .hr-Cross {
    cursor: pointer;
    font-size: 24px;
  }

  @media (max-width: ${Tablet}) {
    & .desktop {
      display: none;
    }
    & .mobile {
      display: flex;
    }
  }
`;

const ImageContainer = styled(HrImageContainer)`
  min-width: 40px;
  width: 40px;
  height: 40px;
`;

const CustomLink = styled.a`
  font-family: 'Open Sans', sans-serif;
  color: ${(props) => props.color || ' #3b3f51'};
  margin: 0;
  padding: 0;
  cursor: pointer;
  font-weight: 400;
  font-size: ${(props) => `${14 + (props.fontSizeSetting || 0)}px`};
  text-decoration: none;
  display: block;
  width: 100%;

  & span {
    font-family: 'Open Sans', sans-serif;
    color: #3b3f51;
    font-size: 14px;
    margin-right: 4px;
  }
`;

const Sidebar = styled.nav`
  display: none;
  max-width: 280px;
  width: 100%;
  height: 100vh;
  position: fixed;
  z-index: 3;
  left: 0;
  top: 0;
  transition: transform 0.5s ease-in-out;
  background-color: ${(props) => props.backgroundColor || HrWhite};
  box-shadow: 5px 0 10px rgb(0 0 0 / 30%);
  &.closed {
    transform: translateX(-100%);
  }

  &.open {
    transform: translateX(0%);
  }

  @media (max-width: ${Tablet}) {
    display: block;
  }
`;

export const Header = ({
  theme,
  maxWidth,
  setShowModalFaqs,
  setShowModalUpdateProfile,
  vacancy,
  action
}) => {
  const [navBar, setNavBar] = useState(false);
  const [t] = useTranslation();
  const { getDataPostulant } = useStore();
  const { darkMode, toggleDarkMode, fontSizeSetting, setFontSizeSetting } = useContext(
    DisplaySettingsContext
  );
  const [iconClassName, setIconClassName] = useState(darkMode ? 'hr-Sun' : 'hr-Moon');

  const changeClassIcon = () => {
    const newClassIcon = iconClassName === 'hr-Sun' ? 'hr-Moon' : 'hr-Sun';
    setIconClassName(newClassIcon);
    toggleDarkMode();
  };

  const setCookie = (name, value) => {
    let domain = window.location.hostname;
    const domainSplit = domain.split('.');
    const dateActual = new Date();
    dateActual.setFullYear(dateActual.getFullYear() + 5);
    domain = domainSplit[0];
    document.cookie =
      name +
      '=' +
      value +
      '; domain=' +
      domain +
      '.com; expires=' +
      dateActual.toUTCString() +
      '; path=/';
  };

  const increaseFontSize = () => {
    if (!(fontSizeSetting < 4)) return;
    setFontSizeSetting((prevFontSize) => prevFontSize + 1);
  };

  const decreaseFontSize = () => {
    if (!(fontSizeSetting > 0)) return;
    setFontSizeSetting((prevFontSize) => prevFontSize - 1);
  };

  useEffect(() => {
    setCookie('font_size', fontSizeSetting);
  }, [fontSizeSetting]);

  return (
    <>
      <MyHeader backgroundColor={darkMode ? '#3B3F51' : theme.tertiaryColor}>
        <HrFlexContainer
          className='desktop'
          justifyContent={vacancy?.status !== 6 ? 'space-between' : 'flex-end'}
          maxWidth={maxWidth}
        >
          {vacancy?.status !== 6 && <BreadCrumb theme={theme} vacancy={vacancy} action={action} />}
          <HrFlexContainer width='auto' flexWrap='nowrap' gap='12px'>
            {typeof setShowModalUpdateProfile === 'function' && (
              <HrIconText
                className='postulation-update-postulation'
                fontWeight={HrFontBold}
                color={darkMode ? 'white' : theme.textRegularColor}
                fontSizeSetting={fontSizeSetting}
                onClick={() => {
                  setShowModalUpdateProfile(true);
                }}
              >
                <span className='hr-Change' style={{ color: darkMode ? 'white' : '' }} />
                {t('postulant.menu.update_profile')}
              </HrIconText>
            )}
            {typeof setShowModalFaqs === 'function' && (
              <HrIconText
                fontWeight={HrFontBold}
                color={darkMode ? 'white' : theme.textRegularColor}
                fontSizeSetting={fontSizeSetting}
                onClick={() => {
                  setShowModalFaqs(true);
                }}
              >
                <span className='hr-Question' style={{ color: darkMode ? 'white' : '' }} />
                {t('postulant.menu.faqs')}
              </HrIconText>
            )}

            <HrButtons
              type='button'
              category={darkMode ? 'actionModeDark' : 'actionMode'}
              padding='0px !important'
              onClick={changeClassIcon}
            >
              <HrIconButton
                color={darkMode ? '' : 'black'}
                style={{
                  fontSize: `${16 + fontSizeSetting}px`,
                  height: `${16 + fontSizeSetting}px`
                }}
                className={iconClassName}
              />
            </HrButtons>

            <HrButtons
              type='button'
              category={darkMode ? 'actionModeDark' : 'actionMode'}
              padding='0px !important'
              onClick={decreaseFontSize}
              className={fontSizeSetting === 0 ? 'disabled-dark-mode' : ''}
            >
              <HrIconButton
                color={darkMode ? '' : 'black'}
                style={{
                  fontSize: `${16 + fontSizeSetting}px`,
                  height: `${16 + fontSizeSetting}px`
                }}
                className='hr-Type-minus'
              />
            </HrButtons>

            <HrButtons
              type='button'
              category={darkMode ? 'actionModeDark' : 'actionMode'}
              padding='0px !important'
              className={fontSizeSetting === 4 ? 'disabled-dark-mode' : ''}
              onClick={increaseFontSize}
            >
              <HrIconButton
                color={darkMode ? '' : 'black'}
                style={{
                  fontSize: `${16 + fontSizeSetting}px`,
                  height: `${16 + fontSizeSetting}px`
                }}
                className='hr-Type-plus'
              />
            </HrButtons>
          </HrFlexContainer>
        </HrFlexContainer>
        <HrFlexContainer className='mobile' justifyContent='space-between'>
          <span
            style={{ color: darkMode ? 'white' : '' }}
            className='hr-menu-burger'
            onClick={() => {
              setNavBar(true);
            }}
            onKeyPress={() => {
              setNavBar(true);
            }}
            role='listbox'
            tabIndex={0}
            aria-label='abrir menu'
          />

          <div style={{ display: 'flex', gap: '11px' }}>
            <HrButtons
              type='button'
              category={darkMode ? 'actionModeDark' : 'actionMode'}
              style={{ width: '40px', height: '40px' }}
              padding='0px !important'
              onClick={changeClassIcon}
            >
              <HrIconButton
                color={darkMode ? '' : 'black'}
                style={{ fontSize: `${16 + fontSizeSetting}px` }}
                className={iconClassName}
              />
            </HrButtons>

            <HrButtons
              type='button'
              category={darkMode ? 'actionModeDark' : 'actionMode'}
              style={{ width: '40px', height: '40px' }}
              padding='0px !important'
              className={fontSizeSetting === 0 ? 'disabled-dark-mode' : ''}
              onClick={decreaseFontSize}
            >
              <HrIconButton
                color={darkMode ? '' : 'black'}
                style={{ fontSize: `${16 + fontSizeSetting}px` }}
                className='hr-Type-minus'
              />
            </HrButtons>

            <HrButtons
              type='button'
              category={darkMode ? 'actionModeDark' : 'actionMode'}
              style={{ width: '40px', height: '40px' }}
              padding='0px !important'
              className={fontSizeSetting === 4 ? 'disabled-dark-mode' : ''}
              onClick={increaseFontSize}
            >
              <HrIconButton
                color={darkMode ? '' : 'black'}
                style={{ fontSize: `${16 + fontSizeSetting}px` }}
                className='hr-Type-plus'
              />
            </HrButtons>
          </div>
        </HrFlexContainer>
      </MyHeader>
      <Sidebar
        className={navBar ? 'open' : 'closed'}
        backgroundColor={darkMode ? '#3B3F51' : HrWhite}
      >
        <HrFlexContainer flexDirection='column'>
          <HrFlexContainer height='60px' padding='0 0 0 16px'>
            <span
              className='hr-Cross'
              onClick={() => {
                setNavBar(false);
              }}
              onKeyPress={() => {
                setNavBar(false);
              }}
              role='listbox'
              tabIndex={0}
              aria-label='Cerrar menu'
              style={{ fontSize: '24px', color: darkMode ? 'white' : '' }}
            />
          </HrFlexContainer>
          {vacancy?.status !== 6 && (
            <HrFlexContainer padding='0 16px' gap='8px' flexWrap='nowrap'>
              <ImageContainer>
                <img
                  src={getDataPostulant?.pathLogoPortal || '/assets/img/app/img-microsite/Logo.png'}
                  alt='Logo de la empresa'
                />
              </ImageContainer>
              <HrTitleH5 fontSizeSetting={fontSizeSetting} color={darkMode ? 'white' : ''}>
                {getDataPostulant?.portalName}
              </HrTitleH5>
            </HrFlexContainer>
          )}
          <Separate margin='0' />
          <HrFlexContainer padding='0 0 0 16px'>
            {vacancy?.status !== 6 && (
              <CustomLink
                fontSizeSetting={fontSizeSetting}
                color={darkMode ? 'white' : ''}
                href={getDataPostulant?.portalLink}
              >
                <span style={{ color: darkMode ? 'white' : '' }} className='hr-Web' />
                {t('postulant.menu.employment_website')}
              </CustomLink>
            )}

            {vacancy?.status !== 6 && getDataPostulant?.carrierSite?.enabled && (
              <CustomLink
                fontSizeSetting={fontSizeSetting}
                color={darkMode ? 'white' : ''}
                href={getDataPostulant?.carrierSite?.url}
              >
                <span style={{ color: darkMode ? 'white' : '' }} className='hr-Glove' />
                {t('postulant.menu.career_site')}
              </CustomLink>
            )}

            {typeof setShowModalUpdateProfile === 'function' && (
              <HrIconText
                fontSizeSetting={fontSizeSetting}
                className='postulation-update-postulation'
                color={darkMode ? 'white' : theme.textRegularColor}
                onClick={() => {
                  setShowModalUpdateProfile(true);
                }}
              >
                <span className='hr-Change' color={darkMode ? 'white' : theme.textRegularColor} />
                {t('postulant.menu.update_profile')}
              </HrIconText>
            )}
            {typeof setShowModalFaqs === 'function' && (
              <HrIconText
                fontSizeSetting={fontSizeSetting}
                color={darkMode ? 'white' : theme.textRegularColor}
                onClick={() => {
                  setShowModalFaqs(true);
                }}
              >
                <span className='hr-Question' color={darkMode ? 'white' : theme.textRegularColor} />
                {t('postulant.menu.faqs')}
              </HrIconText>
            )}
          </HrFlexContainer>
          <Separate margin='0' />
        </HrFlexContainer>
      </Sidebar>
    </>
  );
};
